import { useContext, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { IAdditionalDocumentsRequest, IChecklistItem } from 'src/shared/types/Documents';
import DocumentsGrid from '../DocumentsGrid/DocumentsGrid';
import { Modal } from '@ds/elements/Modal/Modal';
import { AsyncDataHandler, Button, Toast } from '@ds';
import Paginator from '../Paginator/Paginator';
import { buildChecklistDocumentName } from 'src/utils/services/documents';
import { useGetBeneficiary } from 'src/shared/queries/useGetBeneficiary';
import { BeneDocAdditionalContext } from 'src/shared/providers/DocumentUploadContextProvider';
import { DocumentResponse } from 'src/shared/types/Document';
import { addAdditionalDocuments, linkDocumentsToChecklistItem } from 'src/shared/services/document-api/Documents';
import { IOption } from '@ds/elements/Select/Select.types';
import { IToastr } from '@ds/elements/Toast/Toast.types';
import { paginatorDropdownOptions } from 'src/utils/CommonUtils';
import { useGetCaseDocuments } from 'src/shared/queries/useGetCaseDocuments';
import { NoDocumentsFound } from '../NoDocumentsFound/NoDocumentsFound';
import { linkRemoteDocumentsToCase } from 'src/shared/services/fn-api/ImmigrationDocuments';
import { CASE_DOCUMENTS_KEY } from 'src/utils/constants/queryKeys';
import { FeatureFlags } from 'src/utils/constants/FeatureFlags';
import styles from './DocumentsCenterBrowserModal.module.scss';

interface Props {
  isOpen?: boolean;
  onClose?: () => void;
  checklistItem?: IChecklistItem;
  onDocumentsAdded?: () => void;
}

interface TrackingData {
  currentPageNumber: number;
  selectedDocuments: DocumentResponse[];
  isAddingDocuments: boolean;
  rowsPerPage: number;
}

const VISIBILITY_DEPENDENT = ['BENEFICIARY', 'NETWORK_PARTNER_L1', 'NETWORK_PARTNER_L2', 'NETWORK_PARTNER_L3'];
const VISIBILITY_DEFAULT = [
  'BENEFICIARY',
  'HR_CONTACT',
  'HR_LEAD',
  'NETWORK_PARTNER_L1',
  'NETWORK_PARTNER_L2',
  'NETWORK_PARTNER_L3',
];

const DEFAULT_DOCUMENT_TYPE_ID = 14;

const DocumentCenterBrowserModal = (props: Props) => {
  const { isOpen, onClose, checklistItem, onDocumentsAdded } = props;
  const flags = useFlags();
  const toastrRef = useRef<IToastr>(null);
  const { beneId, caseId } = useContext(BeneDocAdditionalContext);
  const { data: beneficiary } = useGetBeneficiary(beneId ?? -1);
  const [checklistName, setChecklistName] = useState('');
  const defaultPageNumber = 1;
  const [trackingData, setTrackingData] = useState<TrackingData>({
    currentPageNumber: defaultPageNumber,
    selectedDocuments: [],
    isAddingDocuments: false,
    rowsPerPage: 10,
  });
  const { data: allDocuments, isLoading } = useGetCaseDocuments(beneId || 0, caseId || 0);
  const queryClient = useQueryClient();

  const documents = allDocuments;

  useEffect(() => {
    if (checklistItem !== undefined) {
      setChecklistName(checklistItem?.checklistDefinition?.name || '');
    } else {
      setChecklistName('Additional Documents');
    }
  }, [checklistItem]);

  useEffect(() => {
    if (!isOpen) {
      setTrackingData((prev) => ({
        ...prev,
        selectedDocuments: [],
        currentPageNumber: defaultPageNumber,
      }));
    }
  }, [isOpen]);

  const refetchCaseDocuments = () => queryClient.refetchQueries({ queryKey: [CASE_DOCUMENTS_KEY, beneId, caseId] });

  const handleDocumentOperationResult = (results: PromiseSettledResult<any>[]) => {
    const hasError = results.some((r) => r.status === 'rejected');
    toastrRef.current?.show({
      severity: hasError ? 'error' : 'success',
      message: hasError ? 'Some document(s) could not be added.' : 'Added document(s) successfully.',
      life: 3000,
      sticky: false,
    });
  };

  const handleDocumentToggle = (doc: DocumentResponse) => {
    const isDocSelected = trackingData.selectedDocuments.some((item) => item.id === doc.id);
    const newSelectedDocuments = isDocSelected
      ? trackingData.selectedDocuments.filter((item) => item.id !== doc.id)
      : [...trackingData.selectedDocuments, doc];
    setTrackingData({
      ...trackingData,
      selectedDocuments: newSelectedDocuments,
    });
  };

  const handlePaginatorPageChange = (evt: IOption | null) => {
    const newPageNumber = evt?.value;
    if (newPageNumber !== undefined) {
      setTrackingData((prev) => ({
        ...prev,
        currentPageNumber: parseInt(newPageNumber.toString()),
      }));
    }
  };

  const renderPaginator = () => {
    if (!documents) {
      return null;
    }

    return (
      <Paginator
        dropdownOptions={paginatorDropdownOptions}
        rowsPerPage={trackingData.rowsPerPage}
        totalRows={documents.length}
        selectedPage={trackingData.currentPageNumber}
        entriesPerRow={1}
        onChangePageInput={handlePaginatorPageChange}
        onSelectRowsPerPageDropdown={(option) => {
          const rowsPerPage = option?.value;
          setTrackingData((prev) => ({
            ...prev,
            rowsPerPage: Number(rowsPerPage),
            currentPageNumber: 1,
          }));
        }}
      />
    );
  };

  let beneficiaryName: string = '';
  if (checklistItem !== undefined)
    beneficiaryName = checklistItem.beneficiaryId
      ? checklistItem.beneficiary?.fullName || ''
      : checklistItem.dependent?.dependentFullName || '';
  else beneficiaryName = beneficiary?.fullName || '';

  const addDocuments = async () => {
    if (!trackingData.selectedDocuments.length) {
      return;
    }
    setTrackingData({
      ...trackingData,
      isAddingDocuments: true,
    });

    if (checklistItem !== undefined) {
      try {
        toastrRef.current?.show({ message: 'Processing...', severity: 'info', sticky: true });
        const visibilitysetting = checklistItem.dependentId ? VISIBILITY_DEPENDENT : VISIBILITY_DEFAULT;
        const promises: Promise<any>[] = [];

        [...trackingData.selectedDocuments].forEach((doc) => {
          if (doc.isRemote) {
            const docRequest = {
              documentName: doc.documentName,
              documentId: Number(doc.id),
              documentApiKey: doc.documentApiKey,
              documentTypeId: DEFAULT_DOCUMENT_TYPE_ID,
              isRemote: doc.isRemote || false,
              checklistItemId: checklistItem.id as number,
              userTypes: visibilitysetting,
              roleType: 'INTERNAL',
              beneId: beneId,
              caseId: String(caseId),
              dependentId: checklistItem.dependentId ? checklistItem.dependentId : undefined,
            };
            promises.push(linkRemoteDocumentsToCase(docRequest));
          } else {
            const docRequest = {
              docName: buildChecklistDocumentName({
                fileName: doc.documentName,
                applicantName: beneficiaryName,
                documentTypeName: checklistItem.checklistDefinition?.documentType?.docTypeName,
              }),
              documentId: Number(doc.id),
              checklistItemId: checklistItem.id as number,
              userTypes: visibilitysetting,
              roleType: 'INTERNAL',
              beneId: beneId,
              caseId: caseId,
              dependentId: checklistItem.dependentId ? checklistItem.dependentId : undefined,
            };
            promises.push(linkDocumentsToChecklistItem([docRequest]));
          }
        });

        if (promises.length > 0) {
          const results = await Promise.allSettled(promises);
          refetchCaseDocuments();
          toastrRef.current?.clear();
          handleDocumentOperationResult(results);
        }

        setTrackingData({
          ...trackingData,
          isAddingDocuments: false,
        });
        if (onClose) {
          setTimeout(() => {
            onClose();
          }, 2000);
        }
        if (onDocumentsAdded) {
          onDocumentsAdded();
        }
      } catch {
        setTrackingData({
          ...trackingData,
          isAddingDocuments: false,
        });
        toastrRef.current?.show({
          severity: 'error',
          message: 'Cannot add document(s).',
          sticky: false,
        });
      }
    } else {
      try {
        toastrRef.current?.show({ message: 'Processing...', severity: 'info', sticky: true });

        if (caseId === undefined) {
          toastrRef.current?.show({
            severity: 'error',
            message: 'Cannot add document(s) without a case ID.',
            sticky: false,
          });
          setTrackingData({
            ...trackingData,
            isAddingDocuments: false,
          });
          return;
        }

        const promises: Promise<any>[] = [];
        [...trackingData.selectedDocuments].forEach((doc) => {
          const docRequest: IAdditionalDocumentsRequest = {
            documentName: doc.documentName,
            documentApiKey: doc.documentApiKey,
            documentId: Number(doc.id),
            caseId: String(caseId),
            userTypes: VISIBILITY_DEFAULT,
            roleType: 'INTERNAL',
            documentTypeId: DEFAULT_DOCUMENT_TYPE_ID,
            isRemote: doc.isRemote || false,
          };
          if (doc.isRemote) {
            promises.push(linkRemoteDocumentsToCase(docRequest));
          } else {
            promises.push(addAdditionalDocuments([docRequest]));
          }
        });

        if (promises.length > 0) {
          const results = await Promise.allSettled(promises);
          refetchCaseDocuments();
          toastrRef.current?.clear();
          handleDocumentOperationResult(results);
        } else {
          toastrRef.current?.clear();
        }

        setTrackingData({
          ...trackingData,
          isAddingDocuments: false,
        });
      } catch {
        setTrackingData({
          ...trackingData,
          isAddingDocuments: false,
        });
        toastrRef.current?.show({
          severity: 'error',
          message: 'Cannot add document(s).',
          sticky: false,
        });
      } finally {
        if (onClose) {
          setTimeout(() => {
            onClose();
          }, 2000);
        }
        if (onDocumentsAdded) {
          setTimeout(() => {
            onDocumentsAdded();
          }, 2000);
        }
      }
    }
  };

  return (
    <Modal
      onModalClosed={onClose}
      visible={isOpen}
      title=""
      className={styles['document-center-browser-modal']}
      fullheight
      fullwidth
    >
      <AsyncDataHandler isLoading={isLoading} loader={{ width: '100vw', height: '100vh' }}>
        <div className={styles['document-center-browser-modal-header']}>
          <div className={styles['header-main']}>
            <p>{checklistName}</p>
            <Button
              disabled={!trackingData.selectedDocuments.length}
              onClick={addDocuments}
              className={styles['add-documents-button']}
            >
              Add Documents
            </Button>
          </div>
          <p className={styles['subtext']}>{`for ${beneficiaryName}`}</p>
        </div>
        {(documents?.length ?? 0) > 0 ? (
          <>
            <div className={styles['document-center-browser-modal-body']}>
              <div className={styles['document-grid-container']}>
                <DocumentsGrid
                  isSelectable
                  documents={(documents || [])
                    .sort((a, b) => (b.createdEpochTime || 0) - (a.createdEpochTime || 0))
                    .slice(
                      (trackingData.currentPageNumber - 1) * trackingData.rowsPerPage,
                      trackingData.currentPageNumber * trackingData.rowsPerPage
                    )}
                  selectedDocuments={trackingData.selectedDocuments}
                  onDocumentToggle={handleDocumentToggle}
                />
              </div>
              {renderPaginator()}
            </div>
          </>
        ) : (
          <NoDocumentsFound />
        )}
        <Toast ref={toastrRef} />
      </AsyncDataHandler>
    </Modal>
  );
};

export default DocumentCenterBrowserModal;
